import React, { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { api, formatApiError } from "@/lib/api";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Eye, EyeOff, Loader2, KeyRound } from "lucide-react";
import { toast } from "sonner";

export default function GantiSandi() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isFirst = params.get("first") === "1" || user?.must_change_password;
  const [cur, setCur] = useState("");
  const [nw, setNw] = useState("");
  const [confirmNw, setConfirmNw] = useState("");
  const [showP, setShowP] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (nw.length < 8) return setError("Kata sandi baru minimal 8 karakter");
    if (nw !== confirmNw) return setError("Konfirmasi kata sandi tidak cocok");
    if (nw === cur) return setError("Kata sandi baru harus berbeda dari kata sandi saat ini");
    setSaving(true);
    try {
      await api.post("/auth/change-password", { current_password: cur, new_password: nw });
      toast.success("Kata sandi berhasil diperbarui");
      // Refresh user info
      const { data } = await api.get("/auth/me");
      setUser(data);
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setSaving(false);
    }
  };

  const body = (
    <div className="max-w-lg mx-auto py-8" data-testid="ganti-sandi-root">
      <Card className="border-2">
        <CardHeader>
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
              <KeyRound className="h-5 w-5 text-primary" />
            </div>
            <div>
              <CardTitle>{isFirst ? "Wajib Ubah Kata Sandi" : "Ubah Kata Sandi"}</CardTitle>
              <CardDescription>
                {isFirst
                  ? "Ini adalah login pertama Anda. Silakan ubah kata sandi sebelum melanjutkan."
                  : "Perbarui kata sandi Anda secara berkala untuk menjaga keamanan akun."}
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <form onSubmit={submit} className="space-y-4" data-testid="ganti-sandi-form">
            <div className="space-y-1.5">
              <Label htmlFor="cur">Kata Sandi Saat Ini</Label>
              <Input
                id="cur" type={showP ? "text" : "password"} required autoComplete="current-password"
                value={cur} onChange={(e) => setCur(e.target.value)}
                data-testid="input-current-password"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="nw">Kata Sandi Baru</Label>
              <div className="relative">
              <Input
                id="nw" type={showP ? "text" : "password"} required autoComplete="new-password"
                value={nw} onChange={(e) => setNw(e.target.value)}
                placeholder="Minimal 8 karakter"
                className="pr-10"
                data-testid="input-new-password"
              />
                <button type="button" onClick={() => setShowP((v) => !v)}
                  className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground p-1"
                  aria-label="Toggle password" data-testid="btn-toggle-newpw">
                  {showP ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="cnw">Konfirmasi Kata Sandi Baru</Label>
              <Input
                id="cnw" type={showP ? "text" : "password"} required autoComplete="new-password"
                value={confirmNw} onChange={(e) => setConfirmNw(e.target.value)}
                data-testid="input-confirm-password"
              />
            </div>
            <div className="text-xs text-muted-foreground bg-muted/50 rounded-md p-3 border border-border">
              <ShieldCheck className="h-3.5 w-3.5 inline mr-1 text-primary" />
              Tips: gunakan gabungan huruf besar, kecil, angka, dan simbol. Jangan pakai kata sandi yang sama dengan akun lain.
            </div>
            {error && (
              <div className="text-sm text-destructive bg-destructive/10 border border-destructive/20 rounded-md px-3 py-2" data-testid="ganti-sandi-error">
                {error}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              {!isFirst && (
                <Button type="button" variant="outline" onClick={() => navigate("/dashboard")} data-testid="btn-cancel-change-pw">
                  Batal
                </Button>
              )}
              <Button type="submit" disabled={saving} data-testid="btn-save-new-password">
                {saving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {saving ? "Menyimpan…" : "Simpan Kata Sandi Baru"}
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );

  // If mustChangePassword, render outside AppShell so user can't navigate away
  if (isFirst) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        {body}
      </div>
    );
  }
  return <AppShell>{body}</AppShell>;
}
